import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '@/lib/apiClient';

const AuthContext = createContext(null);

// Vacío → usa el proxy de craco (/api → :8001). Si se define REACT_APP_BACKEND_URL, lo respeta.
const API = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'lexorbit_token';
const REFRESH_KEY = 'lexorbit_refresh';

// Aplica el access token al header global de axios y guarda ambos tokens
function setAuthHeader(token, refresh) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem(TOKEN_KEY, token);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(false); setLoading(false); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      // Usa el instance `api` para que renueve el token automáticamente si expiró.
      const res = await api.get('/api/usuarios/me');
      setUser({ ...(res.data?.data || res.data), token });
    } catch {
      setAuthHeader(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Login: el backend usa OAuth2PasswordRequestForm (form-urlencoded)
  const login = async (email, password) => {
    const form = new URLSearchParams();
    form.append('username', email);
    form.append('password', password);
    const res = await axios.post(`${API}/api/login`, form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const data = res.data;
    setAuthHeader(data.access_token, data.refresh_token);
    setUser({
      nombre: data.nombre,
      rol: data.rol,
      org_id: data.org_id,
      permisos: data.permisos,
      token: data.access_token,
    });
    return data;
  };

  // Registro: crea organización + admin + trial
  const register = async (name, email, password, orgName, plan, terminos = false) => {
    const [nombre, ...resto] = (name || '').trim().split(' ');
    const apellido = resto.join(' ');
    const res = await axios.post(`${API}/api/registrar-organizacion`, {
      nombre_org: orgName,
      plan: plan || 'independiente',
      nombre: nombre || name,
      apellido,
      email,
      password,
      terminos_aceptados: !!terminos,
    });
    const data = res.data;
    setAuthHeader(data.access_token, data.refresh_token);
    setUser({
      nombre: data.nombre,
      rol: data.rol,
      org_id: data.org_id,
      permisos: data.permisos,
      plan: data.plan,
      trial_fin: data.trial_fin,
      token: data.access_token,
    });
    return data;
  };

  const logout = async () => {
    setAuthHeader(null);
    setUser(false);
  };

  const setGoogleUser = (userData) => setUser(userData);

  // Recuperación de contraseña
  const requestPasswordReset = async (email) => {
    const res = await axios.post(`${API}/api/recuperar-password`, { email });
    return res.data;
  };
  const resetPassword = async (token, nuevaPassword) => {
    const res = await axios.post(`${API}/api/restablecer-password`, { token, nueva_password: nuevaPassword });
    return res.data;
  };

  // Login / registro con Google (credential = ID token de Google Identity Services)
  const loginWithGoogle = async (credential) => {
    const res = await axios.post(`${API}/api/auth/google`, { credential });
    const data = res.data;
    setAuthHeader(data.access_token, data.refresh_token);
    setUser({ nombre: data.nombre, rol: data.rol, org_id: data.org_id, permisos: data.permisos, token: data.access_token });
    return data;
  };
  const registerWithGoogle = async (credential, orgName, plan, terminos = false) => {
    const res = await axios.post(`${API}/api/auth/google/registrar`, { credential, nombre_org: orgName, plan, terminos_aceptados: !!terminos });
    const data = res.data;
    setAuthHeader(data.access_token, data.refresh_token);
    setUser({ nombre: data.nombre, rol: data.rol, org_id: data.org_id, permisos: data.permisos, plan: data.plan, trial_fin: data.trial_fin, token: data.access_token });
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setGoogleUser, checkAuth,
      requestPasswordReset, resetPassword, loginWithGoogle, registerWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
